import lottie from 'lottie-web';
import { preloadImages } from '@data/preload-assets';
import { loadFonts } from '@loaders/FontLoader';

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({
      key: 'LoadScene',
    });
  }

  async preload() {
    this.load.image('overlay', 'assets/GameAssets/overlay.png');

    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');

    await loadFonts();
  }

  create() {
    this.screenOverlay = this.add.image(0, 0, 'overlay').setOrigin(0);

    this.loadingAnim = this.addLoaderLottie(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
    );
    preloadImages(this);
    this.assetsLoad();
    this.endSceneAssetsLoad();

    this.loadVoices(0);

    this.load.start();

    this.load.on(
      'progress',
      function () {
        this.loadingAnim.anim.playSegments([0, 25], true);
      },
      this,
    );

    this.load.on(
      'complete',
      function () {
        console.info('complete');
        this.loadingAnim.anim.loop = false;

        this.loadingAnim.anim.playSegments([26, 41], true);

        this.loadingAnim.anim.addEventListener('complete', () => {
          this.scene.start('StartScene');
        });
      },
      this,
    );
  }

  addLoaderLottie(_x, _y) {
    const lottieRef = this.add
      .dom(_x - this.cameras.main.width / 2, _y - this.cameras.main.height / 2, 'div')
      .setScale(0.7);

    const lottieDiv = lottieRef.node;

    lottieDiv.style.height = '100%';
    lottieDiv.style.width = '100%';

    const lottieAnim = lottie.loadAnimation({
      container: lottieDiv,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    const dataset = { anim: lottieAnim, dom: lottieRef };

    return dataset;
  }

  assetsLoad() {
    // Confetti animation atlas
    this.load.atlas(
      'confetti',
      'assets/GameAssets/checkConfettiAnim.png',
      'assets/GameAssets/checkConfettiAnim.json',
    );

    // Balance Out — Phase 1 animation atlases
    const anims = 'assets/GameAssets/anims';
    this.load.atlas(
      'bluePacketAnim',
      `${anims}/bluePacketAnim.png`,
      `${anims}/bluePacketAnim.json`,
    );
    this.load.atlas(
      'carryBagBackAnim',
      `${anims}/carryBagBackAnim.png`,
      `${anims}/carryBagBackAnim.json`,
    );
    this.load.atlas(
      'carryBagFrontAnim',
      `${anims}/carryBagFrontAnim.png`,
      `${anims}/carryBagFrontAnim.json`,
    );
    this.load.atlas(
      'balanceConfetti',
      `${anims}/confettiAnim.png`,
      `${anims}/confettiAnim.json`,
    );

    // Animated hint icon (lightbulb) — pop / breathe / idle / pop_out
    // sequence ported from the cardistry hint pattern.
    this.load.atlas(
      'hintFinalAnim',
      'assets/GameAssets/hintFinalAnim.png',
      'assets/GameAssets/hintFinalAnim.json',
    );
  }

  // EndScene assets — mirrored from cardistry so balance-out gets the
  // same cinematic spotlight + reward-coin end screen.
  endSceneAssetsLoad() {
    this.load.multiatlas(
      'spotlight',
      'assets/EndSceneAssets/spotlight/spotlightAnimNew.json',
      'assets/EndSceneAssets/spotlight',
    );
    this.load.multiatlas(
      'endConfetti',
      'assets/EndSceneAssets/confetti/confettiAnimNew.json',
      'assets/EndSceneAssets/confetti',
    );
    // Reward reveal background — full-screen 90-frame burst played
    // behind the goldenWeight icon when the student wins.
    this.load.multiatlas(
      'rewardBg',
      'assets/EndSceneAssets/rewardBg.json',
      'assets/EndSceneAssets',
    );

    this.load.image('endNextButton', 'assets/EndSceneAssets/next button.png');
    this.load.image('endScreenBG', 'assets/EndSceneAssets/endSceenBG.png');
    this.load.image('gameOver', 'assets/EndSceneAssets/Game Over.png');
    this.load.image('correctIcon', 'assets/EndSceneAssets/correctIcon.png');
    this.load.image('incorrectIcon', 'assets/EndSceneAssets/incorrectIcon.png');
    this.load.image('retryButton', 'assets/EndSceneAssets/retry button.png');
    this.load.image('hintIcon', 'assets/EndSceneAssets/hintIcon.png');

    this.load.image('okayButton', 'assets/EndSceneAssets/okaybutton.png');
    this.load.image('playAgainButton', 'assets/EndSceneAssets/playAgainButton.png');
    this.load.image(
      'playAgainButtonHover',
      'assets/EndSceneAssets/playAgainButton-hover.png',
    );
    this.load.image('rewardEarned', 'assets/EndSceneAssets/Rewards earned!.png');
    this.load.image('rewardName', 'assets/EndSceneAssets/goldenWeight-text.png');
    this.load.image('goldenWeight', 'assets/EndSceneAssets/goldenWeight.png');
    this.load.image('retryHover', 'assets/EndSceneAssets/RetryHover.png');
    this.load.image('okayHover', 'assets/EndSceneAssets/okayHover.png');
    this.load.image('okayDisabled', 'assets/EndSceneAssets/okayDisabled.png');
    this.load.image('nextHover', 'assets/EndSceneAssets/next hover.png');
    this.load.image('youWon', 'assets/EndSceneAssets/You won.png');
  }

  loadVoices(rangesOrMaxVo = 0, voiceKey = 'voice') {
    const ranges = [];

    if (Array.isArray(rangesOrMaxVo)) {
      if (rangesOrMaxVo.length % 2 !== 0) {
        console.error('Invalid range array: must contain an even number of elements.');
        return;
      }
      // Group the array into ranges [start1, end1, start2, end2, ...]
      for (let i = 0; i < rangesOrMaxVo.length; i += 2) {
        ranges.push([rangesOrMaxVo[i], rangesOrMaxVo[i + 1]]);
      }
    } else {
      // Treat as single number, default range [1, rangesOrMaxVo]
      ranges.push([1, rangesOrMaxVo]);
    }

    // Iterate over all ranges and load voices
    ranges.forEach(([start, end]) => {
      for (let i = start; i <= end; i++) {
        this.load.audio(`voice_${i}`, `assets/GameAssets/audio/${voiceKey}_${i}.mp3`);
        // console.log(`voice_${i}`);
      }
    });
  }

  loadVoicesIncludingSubfolders(
    rangesOrMaxVo = 0,
    subfoldername = '',
    voiceKey = 'voice',
  ) {
    const ranges = [];

    // Check if the input rangeOrMaxVo is an array and handle it
    if (Array.isArray(rangesOrMaxVo)) {
      if (rangesOrMaxVo.length % 2 !== 0) {
        console.error('Invalid range array: must contain an even number of elements.');
        return;
      }
      for (let i = 0; i < rangesOrMaxVo.length; i += 2) {
        ranges.push([rangesOrMaxVo[i], rangesOrMaxVo[i + 1]]);
      }
    } else {
      // Treat as a single number (range [1, rangesOrMaxVo])
      ranges.push([1, rangesOrMaxVo]);
    }

    const basePath = 'assets/GameAssets/audio';
    const subfolderPath = subfoldername ? `${basePath}/${subfoldername}` : basePath;

    // Iterate over the ranges and load the voices
    // Try loading from subfolder first if specified, otherwise from base folder
    ranges.forEach(([start, end]) => {
      for (let i = start; i <= end; i++) {
        const key = `${voiceKey}_${i}`;
        const audioPath = subfoldername
          ? `${subfolderPath}/${key}.mp3`
          : `${basePath}/${key}.mp3`;

        this.load.audio(key, audioPath);
      }
    });

    // Log that the loading process has finished
    console.info('Voice loading completed.');
  }
}
